'use client';
/* eslint-disable */

import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
   Box,
   Button,
   Flex,
   IconButton,
   Text,
   useColorModeValue,
   useDisclosure,
   Select,
   useOutsideClick,
   useBreakpointValue,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, SearchIcon } from '@chakra-ui/icons';
import {
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   getSortedRowModel,
   useReactTable,
} from '@tanstack/react-table';
import 'jspdf-autotable';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { MdNavigateNext, MdNavigateBefore } from 'react-icons/md';
import { Input } from '@chakra-ui/react';
import MobileDataTable from '../../components/MobileDataTable';

// Lazy loaded components
import Card from 'components/card/Card';
import Menu from 'components/menu/MainMenu';
import DeleteDialog from '../../components/DeleteDialog';
import DynamicTable from '../../components/DynamicAllDataTable';
import { useUser } from 'contexts';

// Lazy import form component
const CityMasterForm = React.lazy(() => import('./CityMasterForm'));

// Utility imports - consider moving these to a separate file if not already
import {
   downloadPDF,
   downloadExcel,
   useServerPagination,
   useFilterData,
   usePagination,
} from '../../../../../utils/DataTableUtils';
import { getCities, deleteCity } from '../../../../../redux/actions/city';
import Loader from 'views/admin/default/Loader';

const columnHelper = createColumnHelper();
const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);

export default function CityMaster({ columnsData }) {
   // Color mode values - memoize these
   const backgroundColor = useColorModeValue('white', 'rgba(12, 20, 41, 1)');
   const textColor = useColorModeValue('secondaryGray.900', 'white');
   const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
   const bg = useColorModeValue('#f2f7f2', '#1a254b');
   const icon = useColorModeValue('#000000', '#ffffff');

   // Redux
   const dispatch = useDispatch();
   const cityData = useSelector((state) => state.city?.list || []);
   const totalCount = useSelector((state) => state.city.totalCount || 1);
   const pagination = useSelector((state) => state.city?.pagination);

   // State management
   const [sorting, setSorting] = useState([]);
   const [data, setData] = useState([]);
   const [currentRow, setCurrentRow] = useState(null);
   const [searchQuery, setSearchQuery] = useState('');
   const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
   const [isEditMode, setIsEditMode] = useState(false);
   const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [selectedRowId, setSelectedRowId] = useState(null);
   const [currentPage, setCurrentPage] = useState(1);
   const [limit, setLimit] = useState(10);
   const [expanded, setExpanded] = useState(false);
   const [isEditingPage, setIsEditingPage] = useState(false);
   const [pageInputValue, setPageInputValue] = useState('');
   const [isLoading, setIsLoading] = useState(false);

   // Chakra hooks
   const { isOpen, onOpen, onClose } = useDisclosure();
   const isMobileOrTablet = useBreakpointValue({ base: true, md: false, lg: false });
   const isDesktop = useBreakpointValue({ base: false, md: true });
   const wrapperRef = useRef();

   // User context
   const { user } = useUser();
   const companyId = Number(localStorage.getItem('companyId'));
   const Admin = user?.isAdmin;

   // User permissions
   const cityRight = useMemo(() => {
      return Array.isArray(user?.userRights)
         ? user.userRights.find((right) => right.companyId === companyId)?.city
         : null;
   }, [user?.userRights, companyId]);

   // Filter data based on search query
   const filteredData = useFilterData(data, searchQuery, columnsData);

   // Pagination hooks
   const { getPaginationParams } = usePagination(data, filteredData);

   // Column visibility state
   const [columnVisibility, setColumnVisibility] = useState(
      columnsData.reduce((acc, column) => {
         acc[column.id] = true;
         return acc;
      }, {}),
   );

   // Reset to page 1 when search query changes
   useEffect(() => {
      setCurrentPage(1);
   }, [searchQuery]);

   const debounce = (func, delay) => {
      let timer;
      return function (...args) {
         clearTimeout(timer);
         timer = setTimeout(() => func.apply(this, args), delay);
      };
   };

   const debouncedSearchHandler = React.useCallback(
      debounce((query) => {
         setDebouncedSearchQuery(query);
      }, 500),
      [],
   );

   // Handle search input changes
   const handleSearchChange = (e) => {
      const value = e.target.value.trimStart();
      setSearchQuery(value);
      debouncedSearchHandler(value);
   };

   // Fetch data when dependencies change
   useEffect(() => {
      const fetchMainData = async () => {
         try {
            setIsLoading(true);
            await dispatch(
               getCities({
                  page: currentPage,
                  // limit: limit === pagination?.totalProducts ? undefined : limit,
                  search: debouncedSearchQuery,
                  sortBy: sorting.length ? sorting[0]?.name : 'name',
                  sortOrder: sorting.length ? (sorting[0]?.desc ? 'desc' : 'asc') : 'asc',
               }),
            );
         } catch (error) {
            console.error('Error fetching main data:', error);
         } finally {
            setIsLoading(false);
         }
      };

      // Debounce search to prevent excessive API calls
      const timerId = setTimeout(fetchMainData, 300);
      return () => clearTimeout(timerId);
   }, [dispatch, currentPage, limit, debouncedSearchQuery, sorting, pagination?.totalProducts]);

   // Update local data when redux data changes
   useEffect(() => {
      if (!limit) {
         setData(cityData);
      } else {
         if (cityData.length <= limit) {
            setData(cityData);
         }
      }
   }, [cityData]);

   // Handle outside click for search box
   useOutsideClick({
      ref: wrapperRef,
      handler: () => {
         if (searchQuery === '') setExpanded(false);
      },
   });

   // Memoize the table columns to prevent unnecessary re-renders
   const columns = useMemo(() => {
      return columnsData
         .filter((column) => columnVisibility[column.id])
         .map((column) =>
            columnHelper.accessor(column.id, {
               id: column.id,
               header: () => (
                  <Text
                     justifyContent="space-between"
                     align="center"
                     fontSize={{ sm: '10px', lg: '12px' }}
                     color="gray.400">
                     {column.header}
                  </Text>
               ),
               cell: (info) => <Flex align="center">{info.getValue()}</Flex>,
            }),
         );
   }, [columnsData, columnVisibility]);

   // Memoize the table instance
   const table = useReactTable({
      data,
      columns,
      state: { sorting },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });

   // Event handlers
   const handlePageChange = (directionOrPage) => {
      if (typeof directionOrPage === 'number') {
         // Direct page number navigation
         setCurrentPage(directionOrPage);
      } else {
         // Previous/next navigation
         const newPage = directionOrPage === 'next' ? pagination.nextPage : pagination.previousPage;
         if (newPage) setCurrentPage(newPage);
      }
   };

   const handlePageClick = () => {
      setIsEditingPage(true);
      setPageInputValue(''); // Clear input value when starting to edit
   };

   const handlePageInputSubmit = () => {
      const page = parseInt(pageInputValue);
      if (!isNaN(page) && page >= 1 && page <= pagination?.totalPages) {
         handlePageChange(page);
      }
      setIsEditingPage(false);
      setPageInputValue('');
   };

   const handlePageInputBlur = () => {
      handlePageInputSubmit();
   };

   const handlePageKeyDown = (e) => {
      if (e.key === 'Enter') {
         handlePageInputSubmit();
      } else if (e.key === 'Escape') {
         setIsEditingPage(false);
         setPageInputValue('');
      }
   };

   const handlePageSizeChange = (e) => {
      const value = e.target.value;
      const newLimit = value === 'ALL' ? pagination?.totalProducts : Number(value);
      setLimit(newLimit);
      setCurrentPage(1);
   };

   const handleDelete = async () => {
      if (selectedRowId !== null) {
         try {
            await dispatch(deleteCity(selectedRowId));
            dispatch(
               getCities({
                  page: currentPage,
                  // limit: limit,
                  search: debouncedSearchQuery,
               }),
            );
            setDeleteDialogOpen(false);
         } catch (err) {
            console.error('Failed to delete City:', err);
         }
      }
   };

   const handleDeleteClick = (id) => {
      setSelectedRowId(id);
      setDeleteDialogOpen(true);
   };

   const handleEditClick = (row) => {
      setCurrentRow(row);
      setIsEditMode(true);
      onOpen();
   };

   const handleAddNew = () => {
      setCurrentRow({});
      setIsEditMode(false);
      onOpen();
   };

   return (
      <Box bg={backgroundColor} w="100%" borderRadius="xl" border="1px solid #e2e8f0">
         <Flex
            width={{ base: '100%', md: 'auto' }}
            mb={1}
            mt={1}
            px={{ base: '1px', md: '25px' }}
            justifyContent="space-between"
            align="center"
            overflowX="auto"
            flexWrap="nowrap"
            gap={3}>
            {/* Left Section: Title + Add New Button */}
            <Flex align="center" gap={4} flexShrink={0}>
               <Text
                  color={textColor}
                  ml={{ base: '8px', md: 0 }}
                  fontSize={{ base: '17px', lg: '22px' }}
                  fontWeight="700"
                  lineHeight="100%">
                  City
               </Text>
               {/* {!isMobileOrTablet && (Admin || cityRight?.add) && (
                  <Button
                     onClick={handleAddNew}
                     variant="darkBrand"
                     color="white"
                     fontSize="sm"
                     fontWeight="500"
                     borderRadius="full"
                     p={{ base: '4', lg: '5' }}>
                     Add New
                  </Button>
               )} */}
            </Flex>
            <Flex>
               {/* Search Field */}
               <MotionFlex
                  ref={wrapperRef}
                  align="center"
                  bg={bg}
                  borderRadius="full"
                  cursor="pointer"
                  px={expanded ? 3 : 0}
                  w={expanded ? '200px' : '40px'}
                  h="40px"
                  transition="all 0.3s ease"
                  onClick={() => setExpanded(true)}>
                  <MotionSearchIcon
                     color={icon}
                     animate={{ rotate: expanded ? 0 : 90 }}
                     transition={{ duration: 0.3 }}
                     style={{
                        marginRight: expanded ? '8px' : '10px',
                        marginLeft: expanded ? '0' : '10px',
                     }}
                  />
                  {expanded && (
                     <Input
                        variant="unstyled"
                        placeholder="Search..."
                        value={searchQuery}
                        color={icon}
                        // onChange={(e) => setSearchQuery(e.target.value)}
                        onChange={handleSearchChange}
                        autoFocus
                     />
                  )}
               </MotionFlex>
            </Flex>

            {/* Mobile Floating Add New Button */}
            {/* {isMobileOrTablet && (Admin || cityRight?.add) && (
               <Box position="fixed" bottom="4" right="4" zIndex={999}>
                  <Button
                     onClick={handleAddNew}
                     variant="darkBrand"
                     color="white"
                     fontSize="sm"
                     fontWeight="500"
                     borderRadius="full"
                     p={{ base: '4', lg: '5' }}
                     boxShadow="lg">
                     Add New
                  </Button>
               </Box>
            )} */}
         </Flex>

         {/* Table Rendering */}
         <div className='px-4 pb-4'>
         {isLoading ? (
            <Loader />
         ) : (
            <DynamicTable
               right={cityRight}
               table={table}
               data={cityData}
               emptyMessage="No city data found"
               onEditClick={handleEditClick}
               onDeleteClick={handleDeleteClick}
               canDelete={Admin || cityRight?.delete}
               columnVisibility={columnVisibility}
               pagename={false}
               columnsData={columnsData}
               sorting={sorting}
               limit={limit}
               setCurrentRow={setCurrentRow}
               onOpen={onOpen}
               setIsEditMode={setIsEditMode}
               setDeleteDialogOpen={setDeleteDialogOpen}
               setSelectedRowId={setSelectedRowId}
               debouncedSearchQuery={debouncedSearchQuery}
               type="city"
               entityType={'city'}
            />
         )}
         </div>
         {/* Form Component - Lazy Loaded */}
         <React.Suspense fallback={<Box p={4}>Loading form...</Box>}>
            {isOpen && (
               <CityMasterForm isOpen={isOpen} onClose={onClose} isEditMode={isEditMode} currentRow={currentRow} />
            )}
         </React.Suspense>

         {/* Delete Confirmation Dialog */}
         <DeleteDialog
            isDeleteDialogOpen={isDeleteDialogOpen}
            setDeleteDialogOpen={setDeleteDialogOpen}
            handleDelete={handleDelete}
            title="City"
         />
      </Box>
   );
}
